import { useState, useEffect, useRef } from "react";
import { Mail, Loader2, Sparkles, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const HUBSPOT_PORTAL_ID = "245156642";
const HUBSPOT_FORM_ID = "d52be1f0-7da4-41fd-93d4-8e962fc0585f";
const HUBSPOT_REGION = "na2";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WaitlistSection = () => {
  const formContainerRef = useRef(null);
  const [hubspotLoaded, setHubspotLoaded] = useState(false);
  const [hubspotFailed, setHubspotFailed] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Only try to load HubSpot if we have a valid form ID
    if (HUBSPOT_FORM_ID && HUBSPOT_FORM_ID.length > 10) {
      const script = document.createElement('script');
      script.src = `//js-${HUBSPOT_REGION}.hsforms.net/forms/embed/v2.js`;
      script.charset = 'utf-8';
      script.type = 'text/javascript';
      script.async = true;

      // Timeout fallback - if HubSpot doesn't load in 8 seconds, show custom form
      const timeout = setTimeout(() => {
        if (!hubspotLoaded) {
          setHubspotFailed(true);
        }
      }, 8000);

      script.onload = () => {
        if (window.hbspt && formContainerRef.current) {
          window.hbspt.forms.create({
            region: HUBSPOT_REGION,
            portalId: HUBSPOT_PORTAL_ID,
            formId: HUBSPOT_FORM_ID,
            target: '#hubspot-waitlist-form',
            cssClass: 'hubspot-form-elixirid',
            onFormReady: function() {
              clearTimeout(timeout);
              setHubspotLoaded(true);
            }
          });
        }
      };

      script.onerror = () => {
        clearTimeout(timeout);
        setHubspotFailed(true);
      };

      document.head.appendChild(script);

      return () => clearTimeout(timeout);
    }
  }, [hubspotLoaded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    try {
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, source: 'landing_page' }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail("");
      } else {
        const data = await response.json();
        setSubmitStatus('error');
        setErrorMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Unable to connect. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="waitlist" 
      className="py-20 sm:py-28 lg:py-36 px-6 lg:px-10 bg-gradient-to-b from-slate-950 via-[#020617] to-slate-950 relative overflow-hidden"
      data-testid="waitlist-section"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(139, 92, 246, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>
      
      <div className="absolute -inset-10 sm:-inset-20 bg-violet-600/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="max-w-2xl mx-auto relative z-10 reveal">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm font-semibold">
            <Sparkles className="w-4 h-4" />
            Limited Beta Access
          </div>
        </div>

        {/* Headline */}
        <h2 
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6 text-center"
          data-testid="waitlist-headline"
        >
          Join the Elixir<span className="text-violet-500">ID</span> Waitlist
        </h2>
        
        {/* Description */}
        <p className="text-base lg:text-lg text-slate-400 mb-8 text-center leading-relaxed">
          We're building the governance layer enterprise AI requires. Join our waitlist for early access and help shape the future of AI agent accountability.
        </p>

        {/* Benefits */}
        <div className="flex items-center justify-center gap-6 flex-wrap mb-10 text-sm text-slate-400">
          <span className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
            Priority access to the beta
          </span>
          <span className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
            Direct input on the roadmap
          </span>
          <span className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
            Founding partner pricing
          </span>
        </div>

        {/* Form Container */}
        <div 
          ref={formContainerRef}
          className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6 lg:p-8"
          data-testid="waitlist-form-container"
        >
          {/* Show HubSpot form if loaded, otherwise show custom form */}
          {HUBSPOT_FORM_ID && HUBSPOT_FORM_ID.length > 10 && !hubspotFailed ? (
            <div 
              id="hubspot-waitlist-form" 
              className="hubspot-form-wrapper"
              data-testid="hubspot-form"
            >
              {!hubspotLoaded && (
                <div className="flex items-center justify-center py-8 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Loading form...
                </div>
              )}
            </div>
          ) : (
            /* Custom Waitlist Form */
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="custom-waitlist-form">
              {submitStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mb-4" />
                  <p className="text-lg font-semibold text-white mb-2">You're on the list!</p>
                  <p className="text-sm text-slate-400">We'll reach out when early access is available.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                      Work Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        className="w-full pl-12 pr-4 py-4 bg-slate-950/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errorMessage}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting || !email}
                    className="w-full py-4 bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-600/20"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Joining...
                      </>
                    ) : (
                      'Join the Waitlist'
                    )}
                  </Button>
                </>
              )}
            </form>
          )}
        </div>

        {/* Privacy Note */}
        <p className="text-xs text-slate-600 text-center mt-4">
          No spam. We'll only reach out about ElixirID early access and product updates.
        </p>
      </div>

      {/* HubSpot Form Styles (for when HubSpot is enabled) */}
      <style>{`
        .hubspot-form-wrapper .hs-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .hubspot-form-wrapper .hs-form-field {
          margin-bottom: 0;
        }
        
        .hubspot-form-wrapper .hs-form-field label {
          display: block;
          font-size: 0.75rem;
          font-weight: 600;
          color: #94a3b8;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .hubspot-form-wrapper .hs-input {
          width: 100%;
          padding: 0.875rem 1rem;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid #334155;
          border-radius: 0.75rem;
          color: white;
          font-size: 0.875rem;
          transition: all 0.2s;
        }
        
        .hubspot-form-wrapper .hs-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }
        
        .hubspot-form-wrapper .hs-input::placeholder {
          color: #64748b;
        }
        
        .hubspot-form-wrapper .hs-button {
          width: 100%;
          padding: 1rem 1.5rem;
          background: #8b5cf6;
          color: white;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          border: none;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 0.5rem;
        }
        
        .hubspot-form-wrapper .hs-button:hover {
          background: #7c3aed;
          transform: translateY(-1px);
          box-shadow: 0 10px 40px -10px rgba(139, 92, 246, 0.4);
        }
        
        .hubspot-form-wrapper .hs-error-msgs {
          list-style: none;
          padding: 0;
          margin: 0.25rem 0 0 0;
        }
        
        .hubspot-form-wrapper .hs-error-msgs label {
          color: #f87171;
          font-size: 0.75rem;
        }
        
        .hubspot-form-wrapper .submitted-message {
          text-align: center;
          color: #10b981;
          font-weight: 600;
          padding: 2rem;
        }
      `}</style>
    </section>
  );
};
